import React from 'react'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../i18n/localeUtils'
import { Link } from 'react-router'
import { Box, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { DataGridPro, GridColDef, GridFilterModel, gridClasses } from '@mui/x-data-grid-pro'
import { getMuiDataGridLocaleText } from '../../i18n/localeUtils'

import { companyGridCurrentPageAtom, companySearchTextfieldAtom } from '../../atoms/companyAtom'
import { formatBigMoney, formatDateYYYYMMDD } from '../../packages/utils/numberFormatters'
import { ItalicWrapper } from '../../utils/typography/ItalicWrapper'
import { CompanyName } from './CompanyName'
import { IssueTypeText } from './IssueType'
import { CompanyEntity } from '../../types/CompanyEntity'
import { CustomDataGridToolbar } from '../custom-grid-components/CustomDataGridToolbar'
import { FullHeightBox } from '../UIComponents'
import { translateMillistreamSector } from '../../utils/millistreamSectors'

export const flagPath = (countryCode: string) => `/static/img/flags/${countryCode?.toLowerCase()}.png`

function CustomNoResultsOverlay() {
    return (
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body1'>Inga bolag hittades</Typography>
        </Box>
    )
}

/**
 * @param companies array of company entities
 * @param detailsPath function taking a company and returning a path to the details view
 */
function CompaniesGrid({
    companies,
    detailsPath,
}: {
    companies: CompanyEntity[]
    detailsPath?: (company: { id: string }) => string
}) {
    const [pageSize, setPageSize] = React.useState(10)

    const { t } = useTranslation()
    const { language: locale } = useAppLocale()
    const theme = useTheme()
    const isTabletScreen = useMediaQuery(theme.breakpoints.up('sm'))
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'))

    detailsPath = detailsPath || ((company: { id: string }) => `/companies/${company.id}`)

    const [companyGridCurrentPage, setCompanyGridCurrentPage] = useAtom(companyGridCurrentPageAtom)
    const [searchTextfield, setSearchTextfield] = useAtom(companySearchTextfieldAtom)

    // Initialize filterModel with quickFilterValues and an empty items array
    const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
        items: [], // Ensure items is an empty array initially
        quickFilterValues: [searchTextfield], // Default quick filter value
    })

    const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
        setFilterModel({
            ...newFilterModel,
            items: newFilterModel.items || [], // Ensure items is always an array
        })
        setCompanyGridCurrentPage(0)

        if (newFilterModel.quickFilterValues) {
            setSearchTextfield(newFilterModel.quickFilterValues[0])
        }
    }

    const sortString = (v1: string | null, v2: string | null) => {
        return (v1 ?? '').localeCompare(v2 ?? '')
    }

    const cols: GridColDef<CompanyEntity>[] = [
        {
            field: 'country',
            headerName: t('entities.tableHeaders.country'),
            width: 50,
            valueGetter: (_, row) => row.countryCode,
            sortComparator: sortString,
            display: 'flex',
            renderCell: (params) => (
                <>
                    {params.value && (
                        <Tooltip title={params.value}>
                            <img src={flagPath(params.value)} width={25} />
                        </Tooltip>
                    )}
                </>
            ),
        },
        {
            field: 'name',
            headerName: t('entities.tableHeaders.name'),
            flex: 0.8,
            display: 'flex',
            renderCell: (params) => {
                if (isLargeScreen) {
                    return (
                        <Link to={detailsPath(params.row)}>
                            <CompanyName company={params.row} />
                        </Link>
                    )
                }
                return (
                    <Stack
                        direction='row'
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            paddingY: { xs: 3, md: 4 },
                        }}
                    >
                        {params.row.countryCode ? (
                            <Box
                                sx={{
                                    height: '16px',
                                }}
                            >
                                <img src={flagPath(params.row.countryCode)} width={25} />
                            </Box>
                        ) : null}
                        <Link to={detailsPath(params.row)}>
                            <CompanyName company={params.row} />
                        </Link>
                    </Stack>
                )
            },
        },
        {
            field: 'sector',
            headerName: t('entities.tableHeaders.sector'),
            flex: 0.7,
            valueGetter: (_, row) => (row.isDelisted ? '-' : translateMillistreamSector(1, row.sector1)),
            renderCell: (paramas) => <FullHeightBox>{paramas.value}</FullHeightBox>,
        },
        {
            field: 'marketList',
            headerName: t('entities.tableHeaders.marketList'),
            flex: 0.7,
            valueGetter: (_, row) => (row.marketplaceName ? row.marketplaceName : row.delistedDate),
            sortComparator: sortString,
            display: 'flex',
            renderCell: (params) => {
                const upcomingIpoDate =
                    params.row.shares.every((s) => s.upcomingIpoDate) &&
                    params.row.shares.find((s) => s.upcomingIpoDate)?.upcomingIpoDate
                const hasCancelledIpo = params.row.shares.every((s) => !s.listedDate) && !upcomingIpoDate

                return params.row.isDelisted ? (
                    <ItalicWrapper isItalic>
                        {t('pages.companies.delisted')} ({formatDateYYYYMMDD(params.row.delistedDate, { locale })})
                    </ItalicWrapper>
                ) : upcomingIpoDate ? (
                    <ItalicWrapper isItalic>
                        {t('pages.companies.upcomingIpo', {
                            date: formatDateYYYYMMDD(upcomingIpoDate, { locale }),
                        })}
                    </ItalicWrapper>
                ) : hasCancelledIpo ? (
                    <ItalicWrapper isItalic>{t('pages.companies.cancelledIpo')}</ItalicWrapper>
                ) : (
                    (params.value as string | null)
                )
            },
        },
        {
            field: 'capSize',
            headerName: t('entities.tableHeaders.marketCap'),
            flex: 0.5,
            valueGetter: (_, company) => (company.delistedDate ? 0 : company.marketcap),
            getSortComparator(sortDirection) {
                return (v1: number | null, v2: number | null) => {
                    if (v1 === v2) {
                        // If both values are 0, sort by name as a tiebreaker
                        return 0
                    }

                    if (v1 === 0 || v1 === null) {
                        return 1
                    } else if (v2 === 0 || v2 === null) {
                        return -1
                    }

                    const value1 = v1 ?? 0
                    const value2 = v2 ?? 0

                    if (sortDirection === 'asc') {
                        return value1 - value2
                    } else {
                        return value2 - value1
                    }
                }
            },
            renderCell: (params) => {
                if (params.row.delistedDate) return <FullHeightBox>-</FullHeightBox>
                else if (params.value != 0) {
                    return (
                        <FullHeightBox>
                            {formatBigMoney(params.value, {
                                locale,
                                currencySymbol: params.row.currency,
                            })}
                        </FullHeightBox>
                    )
                } else {
                    return null
                }
            },
        },
        {
            field: 'latestIssue',
            headerName: t('entities.tableHeaders.latestIssue'),
            flex: 1,
            description: t('entities.tableHeaders.latestIssueDescription'),

            valueGetter: (_, row) => {
                return row.latestIssue?.subStartDate || row.latestIssue?.publicationDate || null
            },
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1)
                const date2 = new Date(v2)
                return date1.getTime() - date2.getTime()
            },
            renderCell: (params) => {
                if (params.row.latestIssue) {
                    return (
                        <>
                            <Link to={`/issues/${params.row.latestIssue.id}`}>
                                <IssueTypeText issueType={params.row.latestIssue?.type} />{' '}
                                {formatDateYYYYMMDD(params.value, { locale })}
                            </Link>
                        </>
                    )
                } else {
                    return null
                }
            },
        },
        // { field: 'lei', headerName: 'LEI-kod', flex: 1 },
        // { field: 'registrationNumber', headerName: 'Orgnr', flex: 1 },
    ]

    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }

    const fields = ['name', 'sector']

    if (isLargeScreen) {
        fields.push(...['country', 'marketList', 'latestIssue'])
    }
    if (isTabletScreen) {
        fields.push('capSize')
    }

    return (
        // adapt filter: https://mui.com/x/react-data-grid/filtering/customization/#create-a-custom-operator
        <DataGridPro
            sx={{
                ...disableSelectedSellOutlineStyle,
                '& .MuiDataGrid-main': { width: 0, minWidth: '100%' }, // https://github.com/mui/mui-x/issues/8168#issuecomment-1465784733
                width: '100%',
                '& .even': {
                    backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                },
                '& .odd': {
                    // backgroundColor: theme.palette.rowColor.light, // Use palette color for odd rows
                },
            }}
            localeText={getMuiDataGridLocaleText(locale)}
            disableColumnMenu
            disableRowSelectionOnClick
            rows={companies}
            columns={fields ? cols.filter((c) => fields.includes(c.field)) : cols}
            density='standard'
            getRowHeight={() => (!isLargeScreen ? 'auto' : null)}
            pagination={true}
            pageSizeOptions={[10, 25, 50, 100]}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
            slots={{ noResultsOverlay: CustomNoResultsOverlay, toolbar: CustomDataGridToolbar }}
            paginationModel={{ pageSize, page: companyGridCurrentPage }}
            onPaginationModelChange={({ page, pageSize }) => {
                setCompanyGridCurrentPage(page)
                setPageSize(pageSize)
            }}
            filterModel={filterModel}
            // Capture changes to the filter model
            onFilterModelChange={handleFilterModelChange}
            showToolbar
        />
    )
}

export default CompaniesGrid
